/**
 * Scheduling Types & Validation Schemas
 * Feature: 005-clinic-scheduling
 * Source: specs/005-clinic-scheduling/contracts/scheduling-operations.ts
 *         specs/005-clinic-scheduling/contracts/calendar-queries.ts
 */

import { z } from 'zod'

// ============================================================
// Enums & Constants
// ============================================================

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type Frequency = 'weekly' | 'biweekly' | 'monthly'

export type SchedulingOverlapPolicy =
  | 'allowed'
  | 'not_allowed'
  | 'allowed_with_warning'

export type SessionStatus =
  | 'scheduled'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'no_show'

export type EditScope = 'this_only' | 'this_and_future'

export type CalendarViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'

// ============================================================
// UI Label Maps
// ============================================================

export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
}

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
}

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  scheduled: 'Agendado',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  no_show: 'Não compareceu',
}

export const OVERLAP_POLICY_LABELS: Record<SchedulingOverlapPolicy, string> = {
  allowed: 'Permitido',
  not_allowed: 'Não permitido',
  allowed_with_warning: 'Permitido com aviso',
}

export const SESSION_STATUS_COLORS: Record<
  SessionStatus,
  { bg: string; border: string; text: string }
> = {
  scheduled: { bg: '#3B82F6', border: '#2563EB', text: '#FFFFFF' },
  confirmed: { bg: '#10B981', border: '#059669', text: '#FFFFFF' },
  completed: { bg: '#6B7280', border: '#4B5563', text: '#FFFFFF' },
  cancelled: { bg: '#EF4444', border: '#DC2626', text: '#FFFFFF' },
  no_show: { bg: '#F59E0B', border: '#D97706', text: '#FFFFFF' },
}

// ============================================================
// Entities
// ============================================================

export interface RecurringSchedule {
  id: string
  organization_id: string
  patient_id: string
  day_of_week: DayOfWeek
  start_time: string
  end_time: string
  frequency: Frequency
  effective_from: string
  effective_until: string | null
  location_text: string | null
  is_active: boolean
  created_at: string
  updated_at: string
  professionals: ScheduleProfessional[]
  patient?: { id: string; full_name: string }
}

export interface ScheduleProfessional {
  id: string
  recurring_schedule_id: string
  professional_id: string
  created_at: string
  professional?: {
    id: string
    full_name: string
    specialty: string
  }
}

export interface SessionProfessional {
  id: string
  session_id: string
  professional_id: string
  created_at: string
  professional?: {
    id: string
    full_name: string
    specialty: string
  }
}

export interface Session {
  id: string
  organization_id: string
  patient_id: string
  professional_id: string
  contract_id: string | null
  session_date: string
  start_time: string | null
  end_time: string | null
  session_type: string
  status: SessionStatus
  location_text: string | null
  notes: string | null
  objectives: string | null
  observations: string | null
  next_steps: string | null
  recurring_schedule_id: string | null
  is_exception: boolean
  original_date: string | null
  original_start_time: string | null
  original_end_time: string | null
  created_at: string
  updated_at: string
  patient?: { id: string; full_name: string }
  primary_professional?: { id: string; full_name: string; specialty: string }
  additional_professionals?: SessionProfessional[]
}

// ============================================================
// Calendar Types
// ============================================================

export interface CalendarDateRange {
  start: string
  end: string
}

export interface CalendarEvent {
  id: string
  title: string
  start: string
  end: string
  allDay: false
  backgroundColor: string
  borderColor: string
  textColor: string
  extendedProps: CalendarEventExtendedProps
}

export interface CalendarEventExtendedProps {
  session_id: string
  patient_id: string
  patient_name: string
  professional_ids: string[]
  professional_names: string[]
  status: SessionStatus
  session_type: string
  is_recurring: boolean
  recurring_schedule_id: string | null
  is_exception: boolean
  location_text: string | null
  notes: string | null
  whatsapp_admin_request_count?: number
}

export interface ProfessionalFilterOption {
  id: string
  full_name: string
  specialty: string
  color: string
}

export interface CalendarFilterState {
  selectedProfessionalId: string | null
  currentView: CalendarViewType
  currentDate: string
}

// ============================================================
// Input Types
// ============================================================

export interface CreateRecurringScheduleInput {
  patient_id: string
  day_of_week: DayOfWeek
  start_time: string
  end_time: string
  frequency: Frequency
  effective_from: string
  effective_until?: string | null
  location_text?: string | null
  professional_ids: string[]
}

export interface UpdateRecurringScheduleInput {
  id: string
  day_of_week?: DayOfWeek
  start_time?: string
  end_time?: string
  frequency?: Frequency
  effective_until?: string | null
  location_text?: string | null
  professional_ids?: string[]
  scope: EditScope
}

export interface DeactivateRecurringScheduleInput {
  id: string
  cancel_future_sessions: boolean
}

export interface CreateOneOffAppointmentInput {
  patient_id: string
  session_date: string
  start_time: string
  end_time: string
  session_type: string
  location_text?: string | null
  professional_ids: string[]
  notes?: string
}

export interface CancelSessionInput {
  session_id: string
  scope: EditScope
}

export interface RescheduleSessionInput {
  session_id: string
  new_date: string
  new_start_time: string
  new_end_time: string
  scope: EditScope
}

export interface UpdateOverlapPolicyInput {
  organization_id: string
  policy: SchedulingOverlapPolicy
}

// ============================================================
// Overlap Check Types
// ============================================================

export interface OverlapConflict {
  conflicting_session_id: string
  professional_id: string
  professional_name: string
  patient_name: string
  session_date: string
  start_time: string
  end_time: string
}

export interface OverlapCheckResult {
  has_conflicts: boolean
  conflicts: OverlapConflict[]
  policy: SchedulingOverlapPolicy
}

export interface OverlapCheckInput {
  professional_ids: string[]
  session_date: string
  start_time: string
  end_time: string
  exclude_session_id?: string
}

// ============================================================
// Operation Results
// ============================================================

export interface ScheduleOperationResult<T = void> {
  success: boolean
  data?: T
  error?: string
}

// ============================================================
// Query Types
// ============================================================

export interface FetchCalendarEventsInput {
  organization_id: string
  date_range: CalendarDateRange
  professional_id?: string
  include_cancelled?: boolean
}

export interface CalendarEventsResult {
  events: CalendarEvent[]
  total_count: number
}

export interface PatientScheduleSummary {
  recurring_schedules: Array<{
    id: string
    day_of_week: number
    start_time: string
    end_time: string
    frequency: string
    location_text: string | null
    is_active: boolean
    professionals: Array<{ id: string; full_name: string }>
  }>
  one_off_appointments: Array<{
    id: string
    session_date: string
    start_time: string
    end_time: string
    status: SessionStatus
    location_text: string | null
    professionals: Array<{ id: string; full_name: string }>
  }>
  upcoming_sessions_count: number
}

// ============================================================
// Hook Return Types
// ============================================================

export interface UseRecurringSchedulesReturn {
  schedules: RecurringSchedule[]
  isLoading: boolean
  error: Error | null
  createSchedule: (
    input: CreateRecurringScheduleInput
  ) => Promise<ScheduleOperationResult<RecurringSchedule>>
  updateSchedule: (
    input: UpdateRecurringScheduleInput
  ) => Promise<ScheduleOperationResult<RecurringSchedule>>
  deactivateSchedule: (
    input: DeactivateRecurringScheduleInput
  ) => Promise<ScheduleOperationResult>
}

export interface UseAppointmentsReturn {
  appointments: Session[]
  isLoading: boolean
  error: Error | null
  createOneOff: (
    input: CreateOneOffAppointmentInput
  ) => Promise<ScheduleOperationResult<Session>>
  cancelSession: (input: CancelSessionInput) => Promise<ScheduleOperationResult>
  rescheduleSession: (
    input: RescheduleSessionInput
  ) => Promise<ScheduleOperationResult<Session>>
}

export interface UseCalendarEventsReturn {
  events: CalendarEvent[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
  setDateRange: (range: CalendarDateRange) => void
  setProfessionalFilter: (professionalId: string | null) => void
}

export interface UseOverlapCheckReturn {
  checkOverlap: (input: OverlapCheckInput) => Promise<OverlapCheckResult>
  isChecking: boolean
}

export interface UseOverlapPolicyReturn {
  policy: SchedulingOverlapPolicy
  isLoading: boolean
  updatePolicy: (
    input: UpdateOverlapPolicyInput
  ) => Promise<ScheduleOperationResult>
}

// ============================================================
// Zod Validation Schemas (Form Validation)
// ============================================================

function formatDateForComparison(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getFirstOccurrenceDate(
  dayOfWeek: DayOfWeek,
  effectiveFrom: string
): string | null {
  const [year, month, day] = effectiveFrom.split('-').map(Number) as [
    number | undefined,
    number | undefined,
    number | undefined,
  ]
  if (!year || !month || !day) return null

  const date = new Date(year, month - 1, day)
  if (Number.isNaN(date.getTime())) return null

  const daysUntil = (Number(dayOfWeek) - date.getDay() + 7) % 7
  date.setDate(date.getDate() + daysUntil)

  return formatDateForComparison(date)
}

export const createRecurringScheduleSchema = z
  .object({
    patient_id: z.string().uuid('ID do paciente inválido'),
    day_of_week: z.coerce
      .number()
      .min(0, 'Dia da semana inválido')
      .max(6, 'Dia da semana inválido') as z.ZodType<DayOfWeek>,
    start_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de início inválido (formato: HH:mm)'),
    end_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de término inválido (formato: HH:mm)'),
    frequency: z.enum(['weekly', 'biweekly', 'monthly'], {
      errorMap: () => ({ message: 'Frequência inválida' }),
    }),
    effective_from: z.string().min(1, 'Data de início é obrigatória'),
    effective_until: z.string().nullable().optional(),
    location_text: z
      .string()
      .max(240, 'Local deve ter no máximo 240 caracteres')
      .nullable()
      .optional(),
    professional_ids: z
      .array(z.string().uuid())
      .min(1, 'Pelo menos um profissional deve ser selecionado'),
  })
  .refine((data) => data.end_time > data.start_time, {
    message: 'Horário de término deve ser posterior ao horário de início',
    path: ['end_time'],
  })
  .refine(
    (data) =>
      !data.effective_until || data.effective_until > data.effective_from,
    {
      message: 'Data de término deve ser posterior à data de início',
      path: ['effective_until'],
    }
  )
  .refine(
    (data) => {
      if (!data.effective_until) return true

      const firstOccurrence = getFirstOccurrenceDate(
        data.day_of_week,
        data.effective_from
      )

      return !!firstOccurrence && data.effective_until >= firstOccurrence
    },
    {
      message:
        'Data de término precisa incluir pelo menos uma ocorrência no dia escolhido',
      path: ['effective_until'],
    }
  )

export type CreateRecurringScheduleFormData = z.infer<
  typeof createRecurringScheduleSchema
>

export const createOneOffAppointmentSchema = z
  .object({
    patient_id: z.string().uuid('ID do paciente inválido'),
    session_date: z.string().min(1, 'Data do atendimento é obrigatória'),
    start_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de início inválido (formato: HH:mm)'),
    end_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de término inválido (formato: HH:mm)'),
    session_type: z.string().min(1, 'Tipo de atendimento é obrigatório'),
    location_text: z
      .string()
      .max(240, 'Local deve ter no máximo 240 caracteres')
      .nullable()
      .optional(),
    professional_ids: z
      .array(z.string().uuid())
      .min(1, 'Pelo menos um profissional deve ser selecionado'),
    notes: z.string().optional(),
  })
  .refine((data) => data.end_time > data.start_time, {
    message: 'Horário de término deve ser posterior ao horário de início',
    path: ['end_time'],
  })

export type CreateOneOffAppointmentFormData = z.infer<
  typeof createOneOffAppointmentSchema
>

export const rescheduleSessionSchema = z
  .object({
    session_id: z.string().uuid('ID da sessão inválido'),
    new_date: z.string().min(1, 'Nova data é obrigatória'),
    new_start_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de início inválido (formato: HH:mm)'),
    new_end_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Horário de término inválido (formato: HH:mm)'),
    scope: z.enum(['this_only', 'this_and_future'], {
      errorMap: () => ({ message: 'Escopo de edição inválido' }),
    }),
  })
  .refine((data) => data.new_end_time > data.new_start_time, {
    message: 'Horário de término deve ser posterior ao horário de início',
    path: ['new_end_time'],
  })

export type RescheduleSessionFormData = z.infer<typeof rescheduleSessionSchema>
